import LocalOffer from "@material-design-icons/svg/outlined/local_offer.svg?react";
import Person from "@material-design-icons/svg/outlined/person_outline.svg?react";
import LocationIcon from "@material-design-icons/svg/outlined/location_on.svg?react";
import { Button } from "../ui/button";
import { Link, useRouter } from "@tanstack/react-router";
import { useMemo, useState, useEffect, useRef } from "react";
import ImageGalleryPopUp from "~/components/PopUp/ImageGalleryPopUp";
import { X } from "lucide-react";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import { useSetInquiryLocationMutation } from "~/lib/tanstack-query/hooks/mutations/inquiryLocations";
import { openInquiry } from "~/lib/stores/inquiry";
import {
  groupImagesByCategory,
  LocationWithImages,
} from "~/lib/server-functions/get-locations";
import { ShareIcon } from "~/icons/ShareIcon";
import { HeartIcon } from "~/icons/HeartIcon";
import { HeartFilledIcon } from "~/icons/HeartFilledIcon";
import { LocationDetail } from "./LocationDetail";
import { LocationPlanBadge } from "../LocationPlanBadge";
import { ShareDialog } from "./ShareDialog";
import { locationPriceLevelValues } from "~/db/schema";
import { getStateLabel } from "~/lib/add-location/state-options";
import { parseBilingual } from "~/lib/utils/parse-bilingual";
import noImageFound from "~/assets/no-image.svg";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { getMaxCapacity } from "~/lib/utils/location-capacity";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";

// Helper to safely get description from location
const getLocationDescription = (location: LocationWithImages): string => {
  const parsedDescriptionForm = parseBilingual(location.descriptionForm);
  return parsedDescriptionForm?.german || location.description || "";
};

const sections = [
  { id: "images", label: "Bilder" },
  { id: "information", label: "Informationen" },
  // { id: "reviews", label: "Rezensionen" },
];

interface LocationCardProps {
  location: LocationWithImages;
  compact?: boolean;
  isBookmarked?: boolean;
  onBookmark?: () => void;
  onSelect?: (id: number | undefined) => void;
  selectedLocationId?: number;
  cardRef?: (el: HTMLDivElement | null) => void;
  onHover?: (id: number | undefined) => void;
  showSelectedBorder?: boolean;
  demo?: boolean;
}

export const LocationCard = ({
  location,
  compact,
  isBookmarked,
  onBookmark,
  onSelect,
  selectedLocationId,
  cardRef,
  onHover,
  showSelectedBorder = false,
  demo = false,
}: LocationCardProps) => {
  const groupedLocationImages = groupImagesByCategory(location?.images ?? [], {
    excludePurpose: ["location_logo", "location_contact_person_image"],
  });

  const flatGroupImages = Object.values(groupedLocationImages).flatMap((g) => {
    return g.images.map((img) => ({
      ...img,
      path: img.path as string,
      categoryId: g.categoryId,
      category: g.name,
    }));
  });

  const isSelected = selectedLocationId === location.id;
  const [showDetails, setShowDetails] = useState(isSelected);
  const cardElementRef = useRef<HTMLDivElement>(null);

  // Update showDetails when selectedLocationId changes
  useEffect(() => {
    if (isSelected && !showDetails) {
      setShowDetails(true);
    } else if (!isSelected && showDetails) {
      setShowDetails(false);
    }
  }, [isSelected, showDetails]);

  // Update cardRef callback when element changes
  useEffect(() => {
    if (cardRef) {
      cardRef(cardElementRef.current);
    }
  }, [cardRef]);
  const [showGallery, setShowGallery] = useState(false);
  const [activeImageId, setActiveImageId] = useState<undefined | string>();
  const [showShareDialog, setShowShareDialog] = useState(false);

  const [activeDetailTab, setActiveDetailTab] = useState(sections[0].id);

  const { mutateAsync: setInquiryLocation } = useSetInquiryLocationMutation();
  const router = useRouter();

  const shareUrl = useMemo(
    () =>
      typeof window !== "undefined"
        ? `${window.location.origin}${withLocale(getLocationPath(location))}`
        : "",
    [location],
  );

  return (
    <>
      <div
        ref={cardElementRef}
        className={cn(
          "bg-white flex flex-col",
          compact ? "" : "shadow rounded-md p-2",
          showSelectedBorder && isSelected
            ? "border-2 border-primary-foreground"
            : "",
        )}
        onMouseEnter={() => onHover?.(location.id)}
        onMouseLeave={() => onHover?.(undefined)}
      >
        <div
          className={cn(
            "flex flex-col md:flex-row gap-4 relative md:items-start",
            !compact && "cursor-pointer",
          )}
          onClick={
            !compact
              ? () => {
                setShowDetails(!showDetails);
                if (!showDetails) {
                  onSelect?.(location.id);
                } else {
                  onSelect?.(undefined);
                }
              }
              : undefined
          }
        >
          <div className="relative shrink-0">
            {location.highlightLabel && location.plan === "superior" && (
              <div className="absolute top-1 left-1 bg-neutral-darkest/60 rounded-md text-[#FFDE10] text-xs py-1 px-2">
                {location.highlightLabel}
              </div>
            )}
            <img
              src={
                location.images[0]?.path
                  ? buildImageUrl(location.images[0]!.path!, {
                    size: IMAGE_SIZES.cardThumb,
                  })
                  : noImageFound
              }
              alt=""
              className="rounded-md w-full md:w-[180px] h-[170px] object-cover"
            />
            {location.images.length ? (
              <span className="hidden md:inline absolute bottom-1.5 right-1.5 text-xs bg-white/80 rounded-md px-1.5 py-1">
                1 / {location.images.length}
              </span>
            ) : null}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBookmark?.();
              }}
              type="button"
              className="group/bookmark absolute size-6 top-1.5 right-1.5 cursor-pointer hover:scale-125 transition-transform duration-300"
              title={
                isBookmarked
                  ? "Aus der Merkliste entfernen"
                  : "Zur Merkliste hinzufügen"
              }
            >
              {isBookmarked ? (
                <>
                  <HeartFilledIcon className="w-6 h-6 text-primary" />
                </>
              ) : (
                <>
                  <HeartIcon className="w-6 h-6 text-primary" />
                  <HeartFilledIcon className="w-6 h-6 text-primary group-hover/bookmark:opacity-80 opacity-0 absolute inset-0 transition-opacity duration-300" />
                </>
              )}
            </button>
          </div>
          <div className={cn("block grow", !compact && "cursor-pointer")}>
            <h2 className="font-bold text-2xl mb-1">{location.name}</h2>
            <dl>
              <dt className="text-base font-medium">
                {location.locationTypes.length
                  ? location.locationTypes[0].name
                  : null}
              </dt>
              <div className="text-sm font-normal line-clamp-2">
                <RichTextRenderer richText={getLocationDescription(location)} />
              </div>
              <div className="flex flex-col mt-2 text-sm font-normal">
                <div className="flex items-center gap-1">
                  <dt className="flex items-center gap-2">
                    <LocalOffer className="rotate-90 w-[18px] h-[18px]" />
                    Miete:
                  </dt>
                  <dd className="grow">
                    {(location.priceLevel != null &&
                      locationPriceLevelValues[location.priceLevel]) ||
                      "keine Angabe"}
                  </dd>
                </div>

                <div className="flex items-center gap-1">
                  <dt className="flex items-center gap-2">
                    <Person className="w-[18px] h-[18px]" />
                    max. Personen:
                  </dt>
                  <dd>
                    {(() => {
                      const maxCapacity = getMaxCapacity(location);
                      return maxCapacity ?? "keine Angabe";
                    })()}
                  </dd>
                </div>

                <div className="flex items-center">
                  <dt className="flex items-center gap-2">
                    <LocationIcon className="w-[18px] h-[18px]" />
                    <div className="sr-only">Ort:</div>
                  </dt>
                  <dd className="pl-2">{location.city}</dd>
                </div>
              </div>
            </dl>
          </div>
          {!compact && (
            <div className="md:w-[140px] flex md:flex-col justify-between md:items-stretch md:justify-end gap-1 shrink-0">
              <div className="md:self-end md:grow shrink-0 absolute md:static top-[140px] right-1 md:top-auto md:right-auto">
                <LocationPlanBadge
                  plan={location.plan ?? ""}
                  contrastSave={true}
                  className="size-14 object-contain"
                />
              </div>
              <Button
                variant={"black"}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(!showDetails);
                }}
                asChild
              >
                <Link
                  to={!demo ? withLocale(getLocationPath(location)) : undefined}
                  target={!demo ? "_blank" : undefined}
                  className="md:mt-2 w-full md:w-auto"
                >
                  Details
                </Link>
              </Button>
              <Button
                onClick={async (e) => {
                  if (demo) { return }
                  e.stopPropagation();
                  await setInquiryLocation(location.id);
                  openInquiry("direct", [location.id]);
                }}
                className="w-full md:w-auto"
              >
                Anfragen
              </Button>
            </div>
          )}
        </div>
        {showDetails && !compact && (
          <div className="mt-4 border-t">
            <div className="w-full bg-white z-40 border-b-2 layout-place-full-width layout-content-grid mb-4 flex justify-center relative">
              <button
                onClick={() => setShowShareDialog(true)}
                className="cursor-pointer absolute flex items-center gap-3 left-2 md:left-8 top-1/2 -translate-y-1/2 hover:text-[#060606] hover:cursor-pointer"
              >
                <ShareIcon />
                <span className="hidden md:inline">Teilen</span>
              </button>
              <ShareDialog
                open={showShareDialog}
                onOpenChange={setShowShareDialog}
                location={{
                  name: location.name,
                  images: location.images,
                  locationTypes: location.locationTypes,
                  descriptionForm: parseBilingual(location.descriptionForm),
                  description: location.description ?? null,
                  priceLevel: location.priceLevel ?? null,
                  capacityStanding: location.capacityStanding ?? null,
                  capacitySeated: location.capacitySeated ?? null,
                  seatingBanquet: location.seatingBanquet ?? null,
                  seatingBlock: location.seatingBlock ?? null,
                  seatingParliamentary: location.seatingParliamentary ?? null,
                  seatingRow: location.seatingRow ?? null,
                  seatingTable: location.seatingTable ?? null,
                  seatingUShape: location.seatingUShape ?? null,
                  seatingStanding: location.seatingStanding ?? null,
                  city: location.city ?? null,
                }}
                shareUrl={shareUrl}
              />
              <div className="flex gap-5 py-4 relative w-fit">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveDetailTab(s.id)}
                    className="relative group cursor-pointer px-2"
                  >
                    {s.label}
                    {activeDetailTab === s.id ? (
                      <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full"></span>
                    ) : (
                      <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full scale-x-0 group-hover:scale-x-100"></span>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  onSelect?.(undefined);
                }}
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-dove-gray hover:text-[#060606] hover:cursor-pointer"
              >
                <X />
              </button>
            </div>
            {activeDetailTab === "images" && (
              <>
                {location.images && location.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {location.images.map((img, index) => {
                      const isFifthOrSixth = index % 6 === 4 || index % 6 === 5;
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            setActiveImageId(
                              img.id ? img.id.toString() : undefined,
                            );
                            setShowGallery(true);
                          }}
                          className={`relative w-full h-[170px] max-h-[170px] overflow-hidden rounded-md cursor-pointer ${isFifthOrSixth ? "col-span-2" : ""
                            }`}
                        >
                          <img
                            src={
                              img.path
                                ? buildImageUrl(img.path, {
                                  size: IMAGE_SIZES.galleryThumb,
                                })
                                : noImageFound
                            }
                            alt={img.alt || ""}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {activeDetailTab === "information" && (
              <div className="px-3">
                <LocationDetail
                  description={getLocationDescription(location)}
                  address={{
                    country: location?.country ?? undefined,
                    city: location?.city ?? undefined,
                    postalCode: location?.postalCode ?? undefined,
                    state: location?.state
                      ? getStateLabel(location.state)
                      : undefined,
                    street: location?.street ?? undefined,
                  }}
                  stats={{
                    insideAreaSum: location?.insideAreaSum?.toString() ?? "0",
                    outsideAreaSum: location?.outsideAreaSum?.toString() ?? "0",
                    outsideLargestArea:
                      location?.outsideLargestArea?.toString() ?? "0",
                    // Note: totalRooms field is deprecated - rooms not loaded in list view
                    // Room count is shown on detail page
                    totalRooms: "0",
                    capacitySeated: location?.capacitySeated?.toString() ?? "0",
                    capacityStanding:
                      location?.capacityStanding?.toString() ?? "0",
                    biggestRoom: location?.biggestRoom?.toString() ?? "0",
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {showGallery && (
        <ImageGalleryPopUp
          open={showGallery}
          onOpenChange={(val) => setShowGallery(val)}
          images={flatGroupImages}
          activeImageId={activeImageId}
        />
      )}
    </>
  );
};
